import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "~/../lib/utils";
import { Button, Input } from "~/components/ui";
import { Dialog } from "~/components/common/Dialog";
import type { IconTagMap } from "~/utils/lucide-icons";
import {
  getLoadedIconTags,
  loadIconTags,
  searchIcons,
} from "~/utils/lucide-icons";
import { DynamicIcon, useIconNodes } from "./dynamic-icon";

/** Cell edge in px, including its share of the gap. Drives both the CSS grid and the
 * virtualiser's row height, so the two cannot disagree about where a row sits. */
const CELL_PX = 44;

/** Rows kept mounted beyond the viewport. Cheap here — a cell is one small `<svg>` — and
 * it stops a fast scroll showing blank bands. */
const OVERSCAN_ROWS = 6;

/** The search tags, loaded on first use of a picker and shared afterwards. */
function useIconTags(): IconTagMap | null {
  const [tags, setTags] = useState<IconTagMap | null>(getLoadedIconTags);

  useEffect(() => {
    if (tags) return;

    let cancelled = false;
    loadIconTags()
      .then((loaded) => {
        if (!cancelled) setTags(loaded);
      })
      .catch((error) => {
        console.error("Failed to load Lucide icon tags", error);
      });

    return () => {
      cancelled = true;
    };
  }, [tags]);

  return tags;
}

/** Tracks a element's width so the grid knows how many columns fit. */
function useElementWidth<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  const ref = useCallback((node: T | null) => setElement(node), []);

  useEffect(() => {
    if (!element) return;

    if (!window.ResizeObserver) {
      setWidth(element.getBoundingClientRect().width);
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, width };
}

/**
 * Browse and pick any Lucide icon, by name or by what it means.
 *
 * The whole set, not a curated subset — searching is what makes 1848 tractable, and
 * Lucide's own tags are what make searching work: "clear" finds the eraser and the trash
 * cans, none of which have "clear" in their name.
 *
 * The grid is virtualised because 1848 cells is ~5500 DOM nodes rendered naively, which
 * is enough to make scrolling stutter. Only the visible rows exist at any moment.
 */
export function IconPicker({
  value,
  onSelect,
  allowClear = true,
  className,
}: {
  /** The currently chosen Lucide id, if any. */
  value?: string | null;
  onSelect: (name: string | null) => void;
  /** Whether "no icon" is a valid answer. */
  allowClear?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const icons = useIconNodes();
  const tags = useIconTags();

  const [query, setQuery] = useState("");

  const allNames = useMemo(() => (icons ? Object.keys(icons) : []), [icons]);
  const names = useMemo(
    () => searchIcons(allNames, tags, query),
    [allNames, tags, query],
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const { ref: widthRef, width } = useElementWidth<HTMLDivElement>();

  const columns = Math.max(1, Math.floor(width / CELL_PX));
  const rowCount = Math.ceil(names.length / columns);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CELL_PX,
    overscan: OVERSCAN_ROWS,
  });

  // Back to the top whenever the result set changes, or a search for something near the
  // end of the alphabet would open scrolled past all of its own results.
  useEffect(() => {
    rowVirtualizer.scrollToIndex(0);
    // Re-running on the query rather than on the virtualiser, which is a new object every
    // render.
  }, [query]);

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", className)}>
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" />
        <Input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("common.iconPicker.search", {
            count: allNames.length,
          })}
          className="pl-8"
        />
      </div>

      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <span>{t("common.iconPicker.results", { count: names.length })}</span>
        {allowClear && value ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 px-2"
            onClick={() => onSelect(null)}
          >
            <X className="mr-1 size-3" />
            {t("common.iconPicker.clear")}
          </Button>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto rounded-md border"
      >
        <div ref={widthRef} className="p-1">
          {names.length === 0 ? (
            <p className="text-muted-foreground p-4 text-center text-sm">
              {t("common.iconPicker.noResults", { query })}
            </p>
          ) : (
            <div
              className="relative w-full"
              style={{ height: rowVirtualizer.getTotalSize() }}
            >
              {rowVirtualizer.getVirtualItems().map((row) => {
                const start = row.index * columns;
                const rowNames = names.slice(start, start + columns);

                return (
                  <div
                    key={row.key}
                    className="absolute top-0 left-0 flex w-full"
                    style={{
                      height: row.size,
                      transform: `translateY(${row.start}px)`,
                    }}
                  >
                    {rowNames.map((name) => (
                      <button
                        key={name}
                        type="button"
                        title={name}
                        aria-label={name}
                        aria-pressed={name === value}
                        onClick={() => onSelect(name)}
                        className={cn(
                          "hover:bg-accent flex shrink-0 items-center justify-center rounded-md transition-colors",
                          name === value &&
                            "bg-primary/10 ring-primary ring-2 ring-inset",
                        )}
                        style={{ width: CELL_PX, height: CELL_PX }}
                      >
                        <DynamicIcon name={name} size={20} />
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A button showing the current icon, which opens the picker in a dialog.
 *
 * The form-facing half of the picker, and the one most callers want: config forms are
 * already long, and an 1848-icon grid inlined into one would bury every field under it.
 *
 * The dialog is only mounted while open, so neither the icon geometry nor the tag index
 * is fetched until someone actually goes looking for an icon.
 */
export function IconPickerButton({
  value,
  onChange,
  allowClear = true,
  className,
}: {
  value?: string | null;
  onChange: (name: string | null) => void;
  allowClear?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          className={cn("flex-1 justify-start font-normal", className)}
          onClick={() => setOpen(true)}
        >
          {value ? (
            <DynamicIcon name={value} size={16} className="mr-2" />
          ) : null}
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || t("common.iconPicker.noIcon")}
          </span>
        </Button>

        {allowClear && value ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={t("common.iconPicker.clear")}
            title={t("common.iconPicker.clear")}
            onClick={() => onChange(null)}
          >
            <X className="size-3.5" />
          </Button>
        ) : null}
      </div>

      {open && (
        <Dialog
          open
          externalTrigger
          title={t("common.iconPicker.selectIcon")}
          width="640px"
          onOpenChange={setOpen}
          children={
            <IconPicker
              value={value}
              allowClear={allowClear}
              className="h-[420px]"
              onSelect={(name) => {
                onChange(name);
                setOpen(false);
              }}
            />
          }
        />
      )}
    </>
  );
}
